import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import UploadComponent from '../UploadComponent';
import { postService } from '../../lib/postService';

const MOODS = ['✨ Moment', '💭 Thought', '📸 Photo', '🎬 Reel', '🔗 Link'];

const MobileCreateSheet = ({ onClose }) => {
  const { user: authUser } = useAuth();
  const [text, setText] = useState('');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [mood, setMood] = useState(MOOD => MOODS[0]);
  const [posting, setPosting] = useState(false);
  const uploadRef = useRef(null);

  const currentUser = authUser
    ? { id: authUser.id, avatar: authUser.user_metadata?.avatar_url }
    : null;

  const canPost = text.trim() || mediaUrl;

  const handlePost = async () => {
    if (!currentUser || !canPost) return;
    setPosting(true);
    try {
      await postService.createPost(text, currentUser.id, mediaUrl, mediaType);
      window.dispatchEvent(new Event('new-post-created'));
      onClose();
    } catch (e) {
      console.error('Post failed:', e);
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm md:hidden"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[151] md:hidden rounded-t-3xl overflow-hidden"
        style={{
          background: 'rgba(12,12,16,0.98)',
          backdropFilter: 'blur(40px)',
          borderTop: '0.5px solid rgba(255,255,255,0.1)',
          paddingBottom: 'env(safe-area-inset-bottom, 12px)',
          maxHeight: '90svh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
          <h3 className="text-white font-bold text-base">New Post</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center bg-white/06 hover:bg-white/10">
            <span className="material-symbols-outlined text-white/50 text-[18px]">close</span>
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90svh - 80px)' }}>
          {/* Author row */}
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '1.5px solid rgba(201,169,110,0.4)' }}
            >
              {currentUser?.avatar ? (
                <img src={currentUser.avatar} alt="you" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-white/05">
                  <span className="material-symbols-outlined text-white/40 text-[18px]">person</span>
                </div>
              )}
            </div>

            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share a fragment of your reality…"
              rows={4}
              className="flex-1 bg-transparent text-white text-base placeholder-white/30 resize-none outline-none leading-relaxed"
            />
          </div>

          {/* Mood chips */}
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {MOODS.map((m) => (
              <button
                key={m}
                onClick={() => setMood(m)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: mood === m ? 'rgba(201,169,110,0.2)' : 'rgba(255,255,255,0.05)',
                  border: mood === m ? '1px solid rgba(201,169,110,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  color: mood === m ? '#c9a96e' : 'rgba(255,255,255,0.5)',
                }}
              >
                {m}
              </button>
            ))}
          </div>

          {/* Upload component */}
          <UploadComponent
            triggerRef={uploadRef}
            onUploadSuccess={(url, type) => { setMediaUrl(url); setMediaType(type || 'image'); }}
            compact
          />

          {/* Action bar */}
          <div
            className="flex items-center justify-between pt-3"
            style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-center gap-1">
              {/* Media */}
              <button
                onClick={() => uploadRef.current?.open()}
                className="w-10 h-10 flex items-center justify-center rounded-full active:scale-90 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <span className="material-symbols-outlined text-white/50 text-[20px]">image</span>
              </button>
              {/* Video */}
              <button
                onClick={() => uploadRef.current?.open()}
                className="w-10 h-10 flex items-center justify-center rounded-full active:scale-90 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <span className="material-symbols-outlined text-white/50 text-[20px]">videocam</span>
              </button>
              {/* Reels shortcut */}
              <button
                onClick={() => { onClose(); setTimeout(() => { window.location.href = '/reels'; }, 100); }}
                className="w-10 h-10 flex items-center justify-center rounded-full active:scale-90 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <span className="material-symbols-outlined text-white/50 text-[20px]">play_circle</span>
              </button>
            </div>

            {/* Publish */}
            <button
              onClick={handlePost}
              disabled={!canPost || posting}
              className="px-6 py-2.5 rounded-full font-bold text-sm transition-all active:scale-95 disabled:opacity-40"
              style={{
                background: canPost && !posting
                  ? 'linear-gradient(135deg,#c9a96e,#e8c98a)'
                  : 'rgba(255,255,255,0.1)',
                color: canPost && !posting ? '#000' : 'rgba(255,255,255,0.4)',
              }}
            >
              {posting ? (
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                  Posting…
                </span>
              ) : 'Publish'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileCreateSheet;
